import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useInView } from 'react-intersection-observer'

import { Layout, Header } from 'app/theme/layout'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import NavbarFixed from 'app/theme/NavbarFixed'
import ImageHero from 'app/theme/ImageHero'
import { OccasionPageGrid, OccasionPageGridRow } from '../occasions/shared/OccasionPageGrid'
import CuisineTypes from 'app/theme/sections/CuisineTypes'
import HowItWorks from 'app/theme/sections/HowItWorks'
import HowItWorksLarge from 'app/theme/sections/HowItWorksLarge'
import WhyWorkWithUs from 'app/theme/sections/WhyWorkWithUs'
import FeaturedClients from 'app/theme/sections/FeaturedClients'
import OccasionsSlider from 'app/theme/sections/CateringOccasionsSlider'
import Calculator from 'app/theme/calculator'
import getGoogleMetaTags from './occasionSEOData'

import heroImg from 'assets/img/business-lunch-food-court-hero.jpg'
import gridImage1 from 'assets/img/business-lunch-food-court-1.jpg'
import gridImage2 from 'assets/img/business-lunch-food-court-2.jpg'
import gridImage3 from 'assets/img/business-lunch-food-court-3.jpg'
import gridImage4 from 'assets/img/business-lunch-food-court-4.jpg'

export default function BusinessLunchFoodCourtPage() {
  const { openModal, ...modalState } = useModalState()

  const [ref, inView] = useInView({
    rootMargin: '1px',
    initialInView: true,
  })

  const googleMetaTags = getGoogleMetaTags('businessLunchFoodCourt')

  return (
    <Layout header={<Header occasionMenu />}>
      <Helmet>
        <title>Business Park Lunches - Roaming Hunger</title>
        <meta name="og:title" content="Business Park Lunches"></meta>
        <meta name="description" content="Give your employees a great business lunch by organizing a rotating schedule of food trucks to serve at your office."></meta>
        <script type="application/ld+json">
          {googleMetaTags}
        </script>
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <div ref={ref}>
        <ImageHero
          imageUrl={heroImg}
          title="Business Park Lunches"
          onButtonClick={openModal}
          description={(
            <div>
              Give employees a proper mid-day break by bringing food trucks to your business park or office lot.
              It's an inexpensive way to make employees feel appreciated, engage with each another,
              and increase productivity once they're back at work.
            </div>
          )}
        />
      </div>
      {!inView && (
        <NavbarFixed
          onButtonClick={openModal}
        />
      )}

      <div className="container text-center py-12 px-8">
        <h2 className="text-navy text-4xl leading-tight font-platform">
          More Business Lunch Options with Food Trucks
        </h2>
        <p className="font-light text-gray-900 mt-6">

          Looking to provide your employees with a convenient and rotating lineup of fresh,
          delicious food truck eats? Business park lunches are the solution to work sites
          where food choices are limited.
        </p>
      </div>

      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="We're the Food Truck Experts"
          content="Give your workers the option of amazing food. We secure every food truck in advance, create a shared calendar, and handle all insurance, tax paperwork and operating permits. All you need to do is have on-site parking for the trucks."
          image={gridImage1}
          reverse
        />
        <OccasionPageGridRow
          heading="Lunch Right Where You Want It"
          content="Searching for the closest food options has come to an end. We bring the food trucks to your workplace and create a fun business lunch solution. No need for any permanent infrastructure."
          image={gridImage2}
        />
      </OccasionPageGrid>

      <FeaturedClients
        onButtonClick={openModal}
      />

      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="Countless Choices"
          content="Satisfying your employees has never been easier. With our network of over 23,000 food trucks, we offer a wide assortment of choices, from healthy and vegetarian options to burgers, tacos and pizza. No one will ever forget their lunch money again."
          image={gridImage3}
          reverse
        />
        <OccasionPageGridRow
          heading="Support Local Business"
          content="98% of food trucks are small businesses owned or operated by chef entrepreneurs. Support your community and bring local food trucks to your business park lunch."
          image={gridImage4}
        />
      </OccasionPageGrid>

      <WhyWorkWithUs
        onButtonClick={openModal}
      />

      <CuisineTypes baseUrl='/food-truck-catering' />

      <HowItWorks
        heading="How Business Park Lunch Catering Works"
        onButtonClick={openModal}
      />
      <HowItWorksLarge
        heading="How Business Park Lunch Catering Works"
        onButtonClick={openModal}
      />

      <Calculator
        heading="How Much Does Business Park Lunch Catering Cost?"
        onButtonClick={openModal}
      />

      <OccasionsSlider
        className="bg-white py-12"
      />
    </Layout>
  )
}
